import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Boxes, Check, Plug, X } from "lucide-react";

export default function Integrations() {
  const [available, setAvailable] = useState([]);
  const [connected, setConnected] = useState([]);
  const [active, setActive] = useState(null);
  const [apiKey, setApiKey] = useState("");

  const load = () => {
    api.get("/integrations/available").then((r) => setAvailable(r.data)).catch(() => {});
    api.get("/integrations").then((r) => setConnected(r.data.map((i) => i.provider))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    try {
      await api.post(`/integrations/${active.provider}/connect`, { provider: active.provider, api_key: apiKey, config: {} });
      toast.success(`${active.name} connected`);
      setActive(null); setApiKey(""); load();
    } catch (e) { toast.error("Connection failed"); }
  };
  const disconnect = async (provider) => {
    try { await api.post(`/integrations/${provider}/disconnect`); toast.success("Disconnected"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div>
        <h1 className="text-2xl font-head font-bold">Integrations</h1>
        <p className="text-slate-400 mt-1">Connect external services. Keys are encrypted and never exposed to the browser.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {available.map((it) => {
          const isOn = connected.includes(it.provider);
          return (
            <div key={it.provider} data-testid={`integration-${it.provider}`} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-xl bg-slate-800/60 flex items-center justify-center"><Boxes className="h-5 w-5 text-cyan-400" /></div>
                {isOn && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center gap-1"><Check className="h-3 w-3" /> Connected</span>}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{it.category}</div>
              <h3 className="font-head font-semibold">{it.name}</h3>
              <p className="text-sm text-slate-400 mt-1 mb-4 h-10">{it.desc}</p>
              {isOn ? (
                <button data-testid={`disconnect-${it.provider}`} onClick={() => disconnect(it.provider)}
                  className="w-full h-9 rounded-lg border border-slate-700 text-sm hover:text-rose-400 hover:border-rose-500/40 flex items-center justify-center gap-2 transition-colors"><X className="h-3.5 w-3.5" /> Disconnect</button>
              ) : (
                <button data-testid={`connect-${it.provider}`} onClick={() => setActive(it)}
                  className="w-full h-9 rounded-lg border border-slate-700 text-sm hover:text-emerald-400 hover:border-emerald-500/40 flex items-center justify-center gap-2 transition-colors"><Plug className="h-3.5 w-3.5" /> Connect</button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="connect-dialog">
          <DialogHeader><DialogTitle>Connect {active?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-400">Enter an API key or access token. It is encrypted at rest.</p>
            <input data-testid="integration-api-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder="API key / token"
              className="w-full h-11 px-4 rounded-xl bg-slate-950 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none" />
          </div>
          <DialogFooter>
            <button onClick={() => setActive(null)} className="h-10 px-4 rounded-lg border border-slate-700 text-sm">Cancel</button>
            <button data-testid="confirm-connect" onClick={connect} className="h-10 px-4 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm">Connect</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
